import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { ChevronLeft, ChevronRight, ExternalLink, Download, Play, FileText, Circle, CheckCircle2 } from 'lucide-react';

function LoomEmbed({ url, title }) {
  if (!url) return null;
  const getEmbedUrl = (u) => {
    const match = u.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
    return match ? `https://www.loom.com/embed/${match[1]}` : u;
  };
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden mb-4" data-testid="loom-embed">
      <div className="aspect-video">
        <iframe src={getEmbedUrl(url)} title={title} className="w-full h-full" frameBorder="0" allowFullScreen allow="autoplay; fullscreen; picture-in-picture" />
      </div>
      {title && <div className="px-4 py-3 bg-gray-50 flex items-center gap-2 border-t border-gray-100"><Play className="w-4 h-4 text-red-500" /><span className="text-sm text-gray-700 font-medium">{title}</span></div>}
    </div>
  );
}

function HyperlinkCard({ url, title, description }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-[#F5C518] hover:bg-[#FFFDF0] transition-colors group mb-3" data-testid="hyperlink-card">
      <div className="p-2.5 bg-[#FFF8E1] rounded-lg shrink-0">
        <ExternalLink className="w-5 h-5 text-[#D4A910]" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-gray-900 group-hover:text-[#b8900e] transition-colors">{title}</h4>
        {description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{description}</p>}
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#D4A910] shrink-0 transition-colors" />
    </a>
  );
}

function DocumentCard({ url, title, description, fileSize }) {
  if (!url) return null;
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  return (
    <div className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl mb-3" data-testid="document-card">
      <div className="p-2.5 bg-gray-100 rounded-lg shrink-0">
        <FileText className="w-5 h-5 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <div className="flex items-center gap-2 mt-0.5">
          {description && <p className="text-xs text-gray-500 truncate">{description}</p>}
          {fileSize > 0 && <span className="text-xs text-gray-400 shrink-0">{formatSize(fileSize)}</span>}
        </div>
      </div>
      <a href={url} target="_blank" rel="noopener noreferrer" className="shrink-0 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5" data-testid="download-button">
        <Download className="w-3.5 h-3.5" /> Download
      </a>
    </div>
  );
}

function FigureRow({ title }) {
  return (
    <div className="border border-gray-200 rounded-lg px-5 py-4 mb-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
      {title}
    </div>
  );
}

export default function ResourcePage() {
  const { urlPath } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchResource = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/resources/${urlPath}`);
      setResource(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Resource not found');
    }
    setLoading(false);
  }, [urlPath]);

  useEffect(() => { fetchResource(); }, [fetchResource]);

  const toggleCompletion = async () => {
    if (!resource) return;
    const newCompleted = !resource.completed;
    setResource(prev => ({ ...prev, completed: newCompleted }));
    try {
      await api.put(`/progress/${resource.id}`, { completed: newCompleted });
    } catch {
      setResource(prev => ({ ...prev, completed: !newCompleted }));
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-8 h-8 border-2 border-[#F5C518] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="max-w-3xl mx-auto px-8 py-16 text-center">
      <p className="text-gray-400 text-lg mb-4">{error}</p>
      <button onClick={() => navigate('/dashboard')} className="text-[#D4A910] hover:underline text-sm">Back to Dashboard</button>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-8 py-10 animate-fade-in" data-testid="resource-page">
      {/* Chapter context */}
      {resource.chapter && (
        <p className="text-[#D4A910] text-sm font-semibold mb-2" data-testid="resource-chapter-label">
          Chapter {resource.chapter.number}: {resource.chapter.name}
        </p>
      )}

      {/* Title + Mark Complete */}
      <div className="flex items-start justify-between gap-6 mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 leading-tight" data-testid="resource-title">
          <span className="text-gray-400 font-bold">{resource.url_path}</span>{' '}
          {resource.title}
        </h1>
        <button
          onClick={toggleCompletion}
          className={`shrink-0 flex items-center gap-2 px-4 py-2.5 border-2 rounded-lg text-sm font-medium transition-all ${
            resource.completed 
              ? 'border-green-500 bg-green-50 text-green-700' 
              : 'border-gray-300 bg-white text-gray-600 hover:border-[#F5C518] hover:text-[#D4A910]'
          }`}
          data-testid="resource-completion-toggle"
        >
          {resource.completed ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
          {resource.completed ? 'Completed' : 'Mark Complete'}
        </button>
      </div>

      {/* Description */}
      {resource.short_description && (
        <p className="text-gray-600 text-base leading-relaxed mb-8" data-testid="resource-description">
          {resource.short_description}
        </p>
      )}

      {/* Content Items */}
      {resource.content_items?.length > 0 && (
        <div className="mb-8" data-testid="content-items-section">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Figures & Resources</h2>
          {resource.content_items.map(item => {
            if (item.item_type === 'loom_video') return <LoomEmbed key={item.id} url={item.url} title={item.title} />;
            if (item.item_type === 'hyperlink') return <HyperlinkCard key={item.id} url={item.url} title={item.title} description={item.description} />;
            if (item.item_type === 'document') return <DocumentCard key={item.id} url={item.file_path || item.url} title={item.title} description={item.description} fileSize={item.file_size} />;
            return null;
          })}
        </div>
      )}

      {/* Body Content */}
      {resource.body_content && (
        <div className="prose-content mb-10" data-testid="resource-body-content" dangerouslySetInnerHTML={{ __html: resource.body_content }} />
      )}

      {/* Tags */}
      {resource.tags?.length > 0 && (
        <div className="flex items-center gap-2 mb-10 flex-wrap">
          {resource.tags.map(tag => (
            <span key={tag} className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{tag}</span>
          ))}
        </div>
      )}

      {/* Prev/Next Navigation */}
      <div className="flex items-center justify-between pt-8 border-t border-gray-200" data-testid="resource-navigation">
        {resource.prev_resource ? (
          <Link to={`/resource/${resource.prev_resource.url_path}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:border-gray-400 transition-colors"
            data-testid="prev-resource-link"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </Link>
        ) : <div />}
        {resource.next_resource ? (
          <Link to={`/resource/${resource.next_resource.url_path}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#F5C518] hover:bg-[#D4A910] rounded-lg text-sm font-bold text-black transition-colors"
            data-testid="next-resource-link"
          >
            Next <ChevronRight className="w-4 h-4" />
          </Link>
        ) : <div />}
      </div>
    </div>
  );
}
